import { useContext, useEffect, useState } from "react";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import {
  useSucursalesApi,
  SucursalItem,
  SucursalDto,
} from "../../../services/api/sucursalesApi";
import SucursalDataTable from "../../../components/module/admin/GenericDataTable/SucursalDataTable";
import SucursalModal from "../../../components/module/admin/SucursalModal/SucursalModal";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";

const Sucursales = () => {
  const [items, setItems] = useState<SucursalItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SucursalItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useSucursalesApi();
  const { darkMode } = useContext(DarkModeContext);
  
  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 90 },
    {
      field: "empresa",
      headerName: "Empresa",
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.empresa?.Nombre || "",
    },
    { field: "Nombre", headerName: "Nombre", flex: 1, minWidth: 200 },
    {
      field: "Target",
      headerName: "Objetivo Diario",
      width: 150,
      type: "number",
      valueFormatter: (params) => {
        if (params.value == null) return "";
        return new Intl.NumberFormat("es-CO", {
          style: "currency",
          currency: "COP",
          minimumFractionDigits: 0,
        }).format(params.value);
      },
    },
    { field: "Telefono", headerName: "Teléfono", flex: 1, minWidth: 150 },
    { field: "Direccion", headerName: "Dirección", flex: 1, minWidth: 200 },
    {
      field: "Activo",
      headerName: "Estado",
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const empresaInactiva = !params.row.empresa?.Activo;
        const empresaEliminada = params.row.empresa?.Eliminado;

        return (
          <Tooltip
            title={
              empresaEliminada
                ? "No se puede activar: Empresa eliminada"
                : empresaInactiva
                ? "No se puede activar: Empresa inactiva"
                : ""
            }
          >
            <span>
              <Switch
                checked={params.row.Activo}
                onChange={(e) => handleStatusChange(params.row.Id, e.target.checked)}
                color="primary"
                disabled={empresaEliminada}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const loadItems = async () => {
    const data = await getAll();
    if (Array.isArray(data)) {
      setItems(data);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: SucursalItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción eliminará la sucursal permanentemente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const success = await remove(id);
        if (success) {
          await loadItems();
          toast.success("La sucursal ha sido eliminada.");
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el registro.");
      }
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const sucursal = items.find((item) => item.Id === id);
    if (active && sucursal?.empresa) {
      if (sucursal.empresa.Eliminado) {
        toast.error("No se puede activar la sucursal porque la empresa asociada fue eliminada.");
        return;
      }
    }

    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: `¿Desea ${active ? "activar" : "desactivar"} esta sucursal?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${active ? "activar" : "desactivar"}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const item = items.find((item) => item.Id === id);
        if (item) {
          const updateData: SucursalDto = {
            IdEmpresa: item.IdEmpresa,
            Nombre: item.Nombre,
            Telefono: item.Telefono,
            Direccion: item.Direccion,
            Target: item.Target,
            Activo: active,
            Eliminado: false,
          };
          await update(id, updateData);
          await loadItems();
          toast.success("El estado de la sucursal ha sido actualizado.");
        }
      } catch (err) {
        console.error("Error updating status:", err);
        const error = err as Error;
        Swal.fire("Error", error.message || "No se pudo actualizar el estado.", "error");
      }
    }
  };

  const handleModalSubmit = async (data: SucursalDto) => {
    try {
      if (editingItem) {
        await update(editingItem.Id, data);
      } else {
        await create(data);
      }
      setIsModalOpen(false);
      await loadItems();
      toast.success("Registro guardado correctamente");
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error("No se pudo guardar el registro.");
    }
  };

  return (
    <PageLayout icon={LocationCityIcon} title="Sucursales">
        <div style={{ padding: "24px" }}>
          <SucursalDataTable
            items={items}
            columns={columns}          
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAdd={handleAdd}
            isLoading={isLoading}
          />
          <SucursalModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleModalSubmit}
            title={editingItem ? "Editar Sucursal" : "Nueva Sucursal"}
            initialData={
              editingItem
                ? {
                    IdEmpresa: editingItem.IdEmpresa,
                    Nombre: editingItem.Nombre,
                    Telefono: editingItem.Telefono,
                    Direccion: editingItem.Direccion,
                    Target: editingItem.Target,
                    Activo: editingItem.Activo,
                    Eliminado: editingItem.Eliminado,
                  }
                : undefined
            }
            isLoading={isLoading}
            isEdit={!!editingItem}
          />
        </div>
      </PageLayout>
  );
};

export default Sucursales;